import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, ChevronLeft, ChevronRight, FileText, LogOut, Menu, Shield, Truck, Users, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface NavItem {
  title: string;
  to: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { title: 'Contracts', to: '/contracts', icon: FileText },
  { title: 'Business Units', to: '/business-units', icon: Building2, adminOnly: true },
  { title: 'Suppliers', to: '/suppliers', icon: Truck, adminOnly: true },
  { title: 'Users', to: '/users', icon: Users, adminOnly: true },
  { title: 'Roles', to: '/roles', icon: Shield, adminOnly: true },
];

const COLLAPSE_KEY = 'sidebar-collapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function initials(name?: string) {
  const parts = (name || 'User').trim().split(/\s+/);
  const first = parts[0]?.[0] ?? 'U';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function Sidebar({ collapsed, onToggle, onNavigate }: { collapsed: boolean; onToggle: () => void; onNavigate: () => void }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'Admin' || !!user?.roles?.includes('Admin');
  const items = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const isActive = (to: string) => pathname.startsWith(to) || (to === '/contracts' && pathname === '/');

  return (
    <aside
      className={cn(
        'relative flex h-full w-[260px] shrink-0 flex-col border-r bg-sidebar transition-[width] duration-200',
        collapsed ? 'md:w-[60px]' : 'md:w-[232px]'
      )}
    >
      <div className={cn('flex items-center border-b px-3 py-4', collapsed ? 'justify-between md:justify-center' : 'justify-between')}>
        <Link to="/contracts" onClick={onNavigate} className="flex items-center gap-2.5 px-1" aria-label="De Heus Contract Manager">
          <img src="/deheus-primary-logo.svg" alt="" className="h-10 w-10 shrink-0 object-contain" />
          <div className={cn('leading-tight', collapsed && 'md:hidden')}>
            <div className="text-sm font-semibold text-primary">Contract Manager</div>
            <div className="text-[11px] text-muted-foreground">De Heus</div>
          </div>
        </Link>
        <button
          type="button"
          onClick={onNavigate}
          className="rounded-sm p-1.5 text-muted-foreground hover:bg-accent md:hidden"
          aria-label="Close menu"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <button
        type="button"
        onClick={onToggle}
        className="absolute -right-3 top-[52px] z-10 hidden h-6 w-6 items-center justify-center rounded-full border bg-card text-muted-foreground shadow-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
      </button>

      <nav aria-label="Main" className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        {items.map((item) => {
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              title={collapsed ? item.title : undefined}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                collapsed && 'md:justify-center',
                active ? 'bg-primary font-medium text-primary-foreground' : 'text-foreground hover:bg-accent'
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className={cn(collapsed && 'md:hidden')}>{item.title}</span>
            </Link>
          );
        })}
      </nav>

      <div className={cn('flex items-center gap-2 border-t px-3 py-3', collapsed && 'md:flex-col md:px-1')}>
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
          title={collapsed ? user?.name : undefined}
        >
          {initials(user?.name)}
        </div>
        <div className={cn('min-w-0 flex-1', collapsed && 'md:hidden')}>
          <div className="truncate text-sm font-medium">{user?.name || 'User'}</div>
          <div className="truncate text-[11px] text-muted-foreground">{user?.role || 'User'}</div>
        </div>
        <button
          type="button"
          onClick={logout}
          title="Sign out"
          aria-label="Sign out"
          className="shrink-0 rounded-sm p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}

function Footer() {
  return (
    <footer className="shrink-0 border-t-2 border-success bg-footer px-4 py-2 text-white sm:px-8">
      <div className="flex items-center justify-between gap-4">
        <img className="h-8 w-auto" alt="De Heus" src="/deheus.svg" />
        <div className="text-xs opacity-60">© De Heus {new Date().getFullYear()}</div>
      </div>
    </footer>
  );
}

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      } catch {
        // storage unavailable: keep the in-memory state only
      }
      return !prev;
    });
  };

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-foreground/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 transition-transform duration-200 md:static md:z-auto md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} onNavigate={() => setMobileOpen(false)} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="z-30 shrink-0 border-b bg-card md:hidden">
          <div className="h-1 w-full bg-success" />
          <div className="flex h-14 items-center gap-2 px-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="-ml-1 rounded-sm p-2 text-foreground hover:bg-accent"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/contracts" className="flex items-center gap-2.5">
              <img src="/deheus-primary-logo.svg" alt="De Heus" className="h-8" />
              <span className="text-sm font-semibold text-primary">Contract Manager</span>
            </Link>
          </div>
        </header>

        {/* The only scroll container: sidebar, mobile header and footer stay put, the scrollbar sits at the right edge of this area */}
        {/* `relative` makes main the containing block for absolutely positioned descendants (e.g. Radix's hidden native <select>), so they cannot stretch the document */}
        <main className="relative min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <div className="mx-auto w-full max-w-[88rem] px-4 py-6 sm:px-8 sm:py-8">{children}</div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
